/**
 * Rewritten-text exports (UX-SPEC §17 `export.rewrite`), on the main thread (BENCH-UX-SPEC §3): the rewritten text and
 * the lens live in bench state, not the worker's `ViewModel`, so this mirrors `src/worker/exports.ts`'s pattern (docx
 * lazy-loaded) outside the worker rather than reusing it directly.
 */
import type { Lens2 } from '../../engine/bench/lens2.ts';
import type { ExportFormat } from '../../shared/protocol.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { baseName } from '../../worker/report.ts';
import { LENS } from '../strings-bench.ts';

const TYPES: Record<Exclude<ExportFormat, 'json'>, string> = {
	md: 'text/markdown;charset=utf-8',
	txt: 'text/plain;charset=utf-8',
	html: 'text/html;charset=utf-8',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const escapeHtml = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

function paragraphsOf(text: string): string[] {
	return text
		.split(/\n{2,}/)
		.map((p) => p.trim())
		.filter(Boolean);
}

/** An optional list at the end of an exported rewrite: the passages the lenient standard kept (BENCH-V3-PLAN §7.3). */
export interface Appendix {
	title: string;
	items: string[];
	/**
	 * A second list under its own short title: the passages the editor left as they are (GR2, ED-194), after the lenient keeps.
	 * Absent unless the version was made with the guardrails and the run declined passages.
	 */
	extra?: { title: string; items: string[] };
	/** One closing line about the run (the editorial guardrails, ED-191); absent when the option was off. */
	note?: string;
}

/**
 * The appendix with the run's one-line note: "Editorial guardrails: on (experimental)." when the version was made with them
 * (DESIGN ED-191). With no lenient keeps listed, the note stands alone under its own title. When the editor left passages as
 * they are (`declined`, GR2), they are listed with their reasons under their own title: alone when nothing else is listed,
 * else after the lenient keeps. Off: the appendix as it was.
 */
export function withGuardrailsNote(
	appendix: Appendix | undefined,
	guardrails: boolean | undefined,
	declined: readonly string[] = []
): Appendix | undefined {
	if (!guardrails) return appendix;
	if (appendix) {
		return {
			...appendix,
			...(declined.length > 0
				? { extra: { title: LENS.declinedAppendixTitle, items: [...declined] } }
				: {}),
			note: LENS.guardrailsAppendix,
		};
	}
	if (declined.length > 0) {
		return {
			title: LENS.declinedAppendixTitle,
			items: [...declined],
			note: LENS.guardrailsAppendix,
		};
	}
	return { title: LENS.guardrailsAppendixTitle, items: [], note: LENS.guardrailsAppendix };
}

/**
 * The passages the editor left as they are, each with its reason, in text order (GR2): "¶3: It is a record of what was said."
 * Empty when there is none (always without the guardrails).
 */
export function declinedItems(l: Lens2 | null): string[] {
	return (l?.units ?? [])
		.filter((u) => u.declined !== undefined)
		.sort((a, b) => a.para - b.para)
		.map((u) => LENS.declinedItem(String(u.para + 1), u.declined as string));
}

/**
 * The appendix of the lenient keeps the customer has not reverted, each with its failure report's one-sentence summary, in
 * text order; undefined when there is none.
 */
export function lenientAppendix(l: Lens2 | null, gone: ReadonlySet<string>): Appendix | undefined {
	const items = (l?.units ?? [])
		.filter((u) => u.keptLenient && !gone.has(u.id))
		.map((u) =>
			LENS.appendixItem(
				String(u.para + 1),
				u.channels.join(', '),
				u.report?.summary ?? u.messages[0] ?? ''
			)
		);
	return items.length > 0 ? { title: LENS.appendixTitle, items } : undefined;
}

function renderHtml(title: string, text: string, appendix?: Appendix): string {
	const paras = paragraphsOf(text)
		.map((p) => `<p>${escapeHtml(p)}</p>`)
		.join('\n');
	const extra = appendix
		? `\n<h2>${escapeHtml(appendix.title)}</h2>${
				appendix.items.length > 0
					? `\n<ul>${appendix.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('\n')}</ul>`
					: ''
			}${
				appendix.extra
					? `\n<h2>${escapeHtml(appendix.extra.title)}</h2>\n<ul>${appendix.extra.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('\n')}</ul>`
					: ''
			}${appendix.note ? `\n<p>${escapeHtml(appendix.note)}</p>` : ''}`
		: '';
	return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'"><title>${escapeHtml(title)}</title></head><body>${paras}${extra}</body></html>`;
}

/** The text with the appendix after it, for the plain-text and Markdown exports. */
function withAppendix(text: string, appendix: Appendix | undefined, md: boolean): string {
	if (!appendix) return text;
	const list = appendix.items.map((i) => (md ? `- ${i}` : i)).join('\n');
	const more = appendix.extra
		? `${md ? '## ' : ''}${appendix.extra.title}\n\n${appendix.extra.items.map((i) => (md ? `- ${i}` : i)).join('\n')}`
		: '';
	const body = [list, more, appendix.note ?? ''].filter(Boolean).join('\n\n');
	return `${text.replace(/\s+$/, '')}\n\n${md ? '## ' : ''}${appendix.title}\n\n${body}\n`;
}

async function renderDocx(text: string, appendix?: Appendix): Promise<Blob> {
	const d = await import('docx');
	const { Document, HeadingLevel, Packer, Paragraph, TextRun } = d;
	const body = paragraphsOf(text).map(
		(p) => new Paragraph({ children: [new TextRun({ text: p, font: 'Calibri' })] })
	);
	const extra = appendix
		? [
				new Paragraph({
					heading: HeadingLevel.HEADING_2,
					children: [new TextRun({ text: appendix.title, font: 'Calibri' })],
				}),
				...appendix.items.map(
					(i) => new Paragraph({ children: [new TextRun({ text: i, font: 'Calibri' })] })
				),
				...(appendix.extra
					? [
							new Paragraph({
								heading: HeadingLevel.HEADING_2,
								children: [new TextRun({ text: appendix.extra.title, font: 'Calibri' })],
							}),
							...appendix.extra.items.map(
								(i) => new Paragraph({ children: [new TextRun({ text: i, font: 'Calibri' })] })
							),
						]
					: []),
				...(appendix.note
					? [new Paragraph({ children: [new TextRun({ text: appendix.note, font: 'Calibri' })] })]
					: []),
			]
		: [];
	const doc = new Document({ sections: [{ children: [...body, ...extra] }] });
	return Packer.toBlob(doc);
}

export type RewriteExportFormat = Exclude<ExportFormat, 'json'>;

/** Which text an export is: the latest rewrite, the original (version 0), or version n (the version bar, UD-30). */
export type ExportOf = 'rewrite' | 'original' | { version: number };

/**
 * Builds an export of the rewritten text (txt, md, html or docx); the export filename carries `-rewrite`. With `of: 'original'`
 * it is the original text (version 0, UD-27) instead: the filename ends `-original` and the title says original; with
 * `{ version: n }` it ends `-version-n`. A Markdown `text` is the version's Markdown as made from the upload (UD-30).
 */
export async function buildRewriteExport(
	meta: ViewModel['meta'],
	text: string,
	format: RewriteExportFormat,
	appendix?: Appendix,
	of: ExportOf = 'rewrite'
): Promise<{ blob: Blob; filename: string }> {
	const what =
		of === 'original' ? 'original' : of === 'rewrite' ? 'rewritten' : `version ${of.version}`;
	const tail =
		of === 'original' ? 'original' : of === 'rewrite' ? 'rewrite' : `version-${of.version}`;
	const title = `${baseName(meta)} (${what})`;
	const ext =
		format === 'html' ? '.html' : format === 'docx' ? '.docx' : format === 'md' ? '.md' : '.txt';
	const filename = `${baseName(meta)}-${tail}${ext}`;
	switch (format) {
		case 'txt':
			return {
				blob: new Blob([withAppendix(text, appendix, false)], { type: TYPES.txt }),
				filename,
			};
		case 'md':
			return { blob: new Blob([withAppendix(text, appendix, true)], { type: TYPES.md }), filename };
		case 'html':
			return {
				blob: new Blob([renderHtml(title, text, appendix)], { type: TYPES.html }),
				filename,
			};
		case 'docx':
			return { blob: await renderDocx(text, appendix), filename };
		default:
			throw new Error(`unknown rewrite export format "${format as string}"`);
	}
}
